const SearchToolPopup = {
	popup: null,
	form: null,
	triggers: null,
	closers: null,

	init() {
		this.popup = document.querySelector('[data-component="search-tool-popup"]');
		if (!this.popup) return;

		this.form = this.popup.querySelector('[data-search-tool-form]');
		this.triggers = document.querySelectorAll('[data-search-tool-open], .js-search-tool-open');
		this.closers = this.popup.querySelectorAll('[data-search-tool-close]');

		this.bindEvents();
		this.initCustomSelects();
		this.hideTriggerOverFooter();
	},

	hideTriggerOverFooter() {
		const trigger = document.querySelector('.search-tool-trigger');
		const footer = document.querySelector('.footer-main');
		if (!trigger || !footer || !('IntersectionObserver' in window)) return;

		new IntersectionObserver(([entry]) => {
			trigger.classList.toggle('is-hidden', entry.isIntersecting);
		}).observe(footer);
	},

	getSelectContext(wrap) {
		return {
			wrap,
			hidden: wrap.querySelector('input[type="hidden"]'),
			btn: wrap.querySelector('.search-tool-popup__select'),
			dropdown: wrap.querySelector('.search-tool-popup__select-dropdown'),
			options: wrap.querySelectorAll('.search-tool-popup__select-option'),
			textEl: wrap.querySelector('.search-tool-popup__select-text'),
			placeholder: wrap.dataset.selectPlaceholder || '',
		};
	},

	initCustomSelects() {
		if (!this.form || this.form.dataset.selectsInit === '1') return;
		this.form.dataset.selectsInit = '1';

		this.form.querySelectorAll('[data-custom-select]').forEach((wrap) => {
			const textEl = wrap.querySelector('.search-tool-popup__select-text');
			if (textEl) {
				wrap.dataset.selectPlaceholder = textEl.textContent.trim();
			}
		});

		this.form.addEventListener('click', (e) => this.onFormClick(e));
		this.form.addEventListener('keydown', (e) => this.onFormKeydown(e));

		document.addEventListener(
			'click',
			(e) => {
				if (!this.isOpen()) return;
				const openDropdown = this.form?.querySelector('.search-tool-popup__select-dropdown.is-open');
				if (!openDropdown) return;
				const wrap = openDropdown.closest('[data-custom-select]');
				if (wrap?.contains(e.target)) return;
				this.closeAllSelects();
			},
			true,
		);

		document.addEventListener('keydown', (e) => {
			if (e.key !== 'Escape' || !this.isOpen()) return;

			const openDropdown = this.form?.querySelector('.search-tool-popup__select-dropdown.is-open');
			if (openDropdown) {
				const { btn } = this.getSelectContext(openDropdown.closest('[data-custom-select]'));
				if (btn) {
					e.preventDefault();
					this.closeSelect(btn, openDropdown);
					btn.focus();
					return;
				}
			}

			this.close();
		});

		this.updateSubmitState();
	},

	onFormClick(e) {
		const option = e.target.closest('.search-tool-popup__select-option');
		if (option && this.form.contains(option)) {
			e.preventDefault();
			e.stopPropagation();
			const wrap = option.closest('[data-custom-select]');
			if (!wrap) return;
			const ctx = this.getSelectContext(wrap);
			if (!ctx.hidden || !ctx.btn || !ctx.dropdown || !ctx.textEl) return;
			this.selectOption(ctx, option.dataset.value || '', option.textContent.trim());
			return;
		}

		const btn = e.target.closest('.search-tool-popup__select');
		if (!btn || !this.form.contains(btn)) return;

		e.preventDefault();
		e.stopPropagation();

		const wrap = btn.closest('[data-custom-select]');
		if (!wrap) return;

		const { dropdown } = this.getSelectContext(wrap);
		if (!dropdown) return;

		const willOpen = btn.getAttribute('aria-expanded') !== 'true';
		this.closeAllSelectsExcept(wrap);
		if (willOpen) {
			this.openSelect(wrap, btn, dropdown);
		} else {
			this.closeSelect(btn, dropdown);
		}
	},

	onFormKeydown(e) {
		const btn = e.target.closest('.search-tool-popup__select');
		if (btn && this.form.contains(btn)) {
			const wrap = btn.closest('[data-custom-select]');
			if (!wrap) return;
			const ctx = this.getSelectContext(wrap);
			if (ctx.dropdown && ctx.options.length) {
				this.handleTriggerKeydown(e, ctx);
			}
			return;
		}

		if (e.target.classList?.contains('search-tool-popup__select-option')) {
			const wrap = e.target.closest('[data-custom-select]');
			if (!wrap || !this.form.contains(e.target)) return;
			const ctx = this.getSelectContext(wrap);
			if (ctx.hidden && ctx.btn && ctx.dropdown && ctx.textEl && ctx.options.length) {
				this.handleDropdownKeydown(e, ctx);
			}
		}
	},

	handleTriggerKeydown(e, { wrap, btn, dropdown, options }) {
		const isOpen = dropdown.classList.contains('is-open');

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			this.closeAllSelectsExcept(wrap);
			if (!isOpen) {
				this.openSelect(wrap, btn, dropdown);
			} else {
				this.closeSelect(btn, dropdown);
			}
			return;
		}

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (!isOpen) {
				this.closeAllSelectsExcept(wrap);
				this.openSelect(wrap, btn, dropdown);
			}
			this.highlightOption(options, 0);
			return;
		}

		if (e.key === 'ArrowUp' && isOpen) {
			e.preventDefault();
			this.highlightOption(options, options.length - 1);
		}
	},

	handleDropdownKeydown(e, ctx) {
		const { btn, dropdown, options } = ctx;
		if (!dropdown.classList.contains('is-open')) return;

		const idx = this.getHighlightedIndex(options);
		const max = options.length - 1;

		if (e.key === 'ArrowDown') {
			e.preventDefault();
			this.highlightOption(options, idx < 0 ? 0 : Math.min(idx + 1, max));
			return;
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			this.highlightOption(options, idx <= 0 ? max : idx - 1);
			return;
		}

		if (e.key === 'Enter') {
			e.preventDefault();
			const target = options[idx >= 0 ? idx : 0];
			if (target) {
				this.selectOption(ctx, target.dataset.value || '', target.textContent.trim());
			}
			return;
		}

		if (e.key === 'Escape') {
			e.preventDefault();
			e.stopPropagation();
			this.closeSelect(btn, dropdown);
			btn.focus();
		}
	},

	/* ---- Dropdown open / close ---- */

	openSelect(wrap, btn, dropdown) {
		btn.setAttribute('aria-expanded', 'true');
		dropdown.classList.add('is-open');
		wrap.classList.add('is-dropdown-open');
		this.popup.classList.add('has-select-dropdown-open');

		const btnRect = btn.getBoundingClientRect();
		const wrapRect = wrap.getBoundingClientRect();
		const contentEl = this.popup.querySelector('.search-tool-popup__content');
		const contentRect = contentEl ? contentEl.getBoundingClientRect() : { bottom: window.innerHeight, right: window.innerWidth };

		const spaceBelow = contentRect.bottom - btnRect.bottom;
		const dropdownHeight = Math.min(15 * 16, window.innerHeight * 0.5);
		dropdown.classList.toggle('opens-up', spaceBelow < dropdownHeight + 8);

		const dropdownWidth = Math.min(18.75 * 16, window.innerWidth - 48);
		const spaceRight = contentRect.right - wrapRect.left;
		dropdown.classList.toggle('opens-left', spaceRight < dropdownWidth);

		this.clearHighlight(wrap);
		const selected = wrap.querySelector('.search-tool-popup__select-option.is-selected');
		if (selected) {
			selected.classList.add('is-highlighted');
			this.scrollOptionIntoView(selected, dropdown);
		} else {
			wrap.querySelector('.search-tool-popup__select-option')?.classList.add('is-highlighted');
		}
	},

	closeSelect(btn, dropdown) {
		btn.setAttribute('aria-expanded', 'false');
		dropdown.classList.remove('is-open');
		dropdown.classList.remove('opens-up');
		dropdown.classList.remove('opens-left');

		const wrap = btn.closest('[data-custom-select]');
		if (wrap) {
			this.clearHighlight(wrap);
			wrap.classList.remove('is-dropdown-open');
		}

		if (!this.form?.querySelector('.search-tool-popup__select-dropdown.is-open')) {
			this.popup.classList.remove('has-select-dropdown-open');
		}
	},

	closeAllSelectsExcept(exceptWrap) {
		this.form?.querySelectorAll('[data-custom-select]').forEach((wrap) => {
			if (wrap === exceptWrap) return;
			const btn = wrap.querySelector('.search-tool-popup__select');
			const dropdown = wrap.querySelector('.search-tool-popup__select-dropdown');
			if (btn && dropdown) this.closeSelect(btn, dropdown);
		});
	},

	closeAllSelects() {
		this.form?.querySelectorAll('[data-custom-select]').forEach((wrap) => {
			const btn = wrap.querySelector('.search-tool-popup__select');
			const dropdown = wrap.querySelector('.search-tool-popup__select-dropdown');
			if (btn && dropdown) this.closeSelect(btn, dropdown);
		});
	},

	/* ---- Option highlight / select ---- */

	selectOption({ hidden, btn, dropdown, options, textEl, placeholder }, value, label) {
		hidden.value = value;
		textEl.textContent = value ? label : placeholder;
		btn.classList.toggle('is-selected', Boolean(value));

		options.forEach((opt) => {
			const isMatch = value !== '' && (opt.dataset.value || '') === value;
			opt.classList.toggle('is-selected', isMatch);
			opt.setAttribute('aria-selected', isMatch ? 'true' : 'false');
		});

		this.closeSelect(btn, dropdown);
		btn.focus();
		this.updateSubmitState();
	},

	updateSubmitState() {
		if (!this.form) return;
		const submitBtn = this.form.querySelector('.search-tool-popup__submit');
		if (!submitBtn) return;

		const allFilled = Array.from(this.form.querySelectorAll('[data-custom-select] input[type="hidden"]')).every(
			(input) => input.value !== '',
		);

		submitBtn.disabled = !allFilled;
	},

	clearHighlight(wrap) {
		wrap.querySelectorAll('.search-tool-popup__select-option.is-highlighted').forEach((el) => {
			el.classList.remove('is-highlighted');
		});
	},

	getHighlightedIndex(options) {
		return Array.from(options).findIndex((o) => o.classList.contains('is-highlighted'));
	},

	highlightOption(options, index) {
		options.forEach((o, i) => o.classList.toggle('is-highlighted', i === index));
		const target = options[index];
		if (target) {
			target.focus({ preventScroll: true });
			const dropdown = target.closest('.search-tool-popup__select-dropdown');
			if (dropdown) this.scrollOptionIntoView(target, dropdown);
		}
	},

	scrollOptionIntoView(option, dropdown) {
		const dRect = dropdown.getBoundingClientRect();
		const oRect = option.getBoundingClientRect();
		if (oRect.bottom > dRect.bottom) {
			dropdown.scrollTop += oRect.bottom - dRect.bottom;
		} else if (oRect.top < dRect.top) {
			dropdown.scrollTop -= dRect.top - oRect.top;
		}
	},

	/* ---- Popup open / close / submit ---- */

	bindEvents() {
		this.triggers.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				this.open();
			});
		});

		this.closers.forEach((el) => {
			el.addEventListener('click', () => this.close());
		});

		if (this.form) {
			this.form.addEventListener('submit', (e) => {
				e.preventDefault();
				this.submit();
			});
		}
	},

	isOpen() {
		return this.popup?.getAttribute('aria-hidden') === 'false';
	},

	open() {
		this.popup.setAttribute('aria-hidden', 'false');
		document.body.classList.add('search-tool-open');
		const firstBtn = this.popup.querySelector('[data-custom-select] .search-tool-popup__select');
		if (firstBtn) firstBtn.focus();
	},

	close() {
		this.closeAllSelects();
		this.resetForm();
		this.popup.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('search-tool-open');
	},

	resetForm() {
		if (!this.form) return;
		this.form.querySelectorAll('[data-custom-select]').forEach((wrap) => {
			const ctx = this.getSelectContext(wrap);
			if (!ctx.hidden || !ctx.btn || !ctx.textEl) return;
			ctx.hidden.value = '';
			ctx.textEl.textContent = ctx.placeholder;
			ctx.btn.classList.remove('is-selected');
			ctx.options.forEach((opt) => {
				opt.classList.remove('is-selected');
				opt.setAttribute('aria-selected', 'false');
			});
		});
		this.updateSubmitState();
	},

	submit() {
		const config = window.pardesSearchTool || {};
		const resultsUrl = config.resultsUrl;

		if (!resultsUrl) {
			console.warn('[SearchToolPopup] Submit ignored — resultsUrl is empty. Set Search Tool Settings → Results page in wp-admin.');
			return;
		}

		const formData = new FormData(this.form);
		const params = new URLSearchParams();

		for (const [key, value] of formData.entries()) {
			if (value) {
				params.set(key, value);
			}
		}

		const separator = resultsUrl.includes('?') ? '&' : '?';
		window.location.href = resultsUrl + separator + params.toString();
	},
};

export default SearchToolPopup;
